import type { BudgetPeriodType } from '@/api/budget'
import { financialYearLabel, type FyStartMonth } from './financialYear'

export type { FyStartMonth }

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// Matches the backend's PeriodParam (#390): years, quarters and halves follow the household's
// financial year. "2026" is the year starting in 2026 — with April–March that's Apr 2026–Mar 2027,
// 2026-Q1 is Apr–Jun and 2026-H2 is Oct 2026–Mar 2027. With January–December they're calendar periods.
const MONTHS_IN_PERIOD: Record<BudgetPeriodType, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
}

function periodStartDate(type: BudgetPeriodType, value: string, fy: FyStartMonth): Date {
  switch (type) {
    case 'MONTHLY': {
      const [y, m] = value.split('-').map(Number)
      return new Date(y, m - 1, 1)
    }
    case 'QUARTERLY': {
      const [y, q] = value.split('-Q').map(Number)
      return new Date(y, fy - 1 + (q - 1) * 3, 1)
    }
    case 'HALF_YEARLY': {
      const [y, h] = value.split('-H').map(Number)
      return new Date(y, fy - 1 + (h - 1) * 6, 1)
    }
    case 'YEARLY':
      return new Date(Number(value), fy - 1, 1)
  }
}

export function periodValueForDate(type: BudgetPeriodType, date: Date, fy: FyStartMonth): string {
  const y = date.getFullYear()
  const m = date.getMonth()
  const fyYear = m >= fy - 1 ? y : y - 1
  const monthInFy = (m - (fy - 1) + 12) % 12
  switch (type) {
    case 'MONTHLY':
      return `${y}-${String(m + 1).padStart(2, '0')}`
    case 'QUARTERLY':
      return `${fyYear}-Q${Math.floor(monthInFy / 3) + 1}`
    case 'HALF_YEARLY':
      return `${fyYear}-H${monthInFy < 6 ? 1 : 2}`
    case 'YEARLY':
      return `${fyYear}`
  }
}

export function currentPeriodValue(type: BudgetPeriodType, fy: FyStartMonth): string {
  return periodValueForDate(type, new Date(), fy)
}

// Preserves the point in time when switching granularity — e.g. August 2026 (Monthly) becomes
// the quarter containing August, not whatever quarter today happens to be in.
export function convertPeriod(
  fromType: BudgetPeriodType,
  value: string,
  toType: BudgetPeriodType,
  fy: FyStartMonth
): string {
  if (fromType === toType) return value
  return periodValueForDate(toType, periodStartDate(fromType, value, fy), fy)
}

export function shiftPeriod(
  type: BudgetPeriodType,
  value: string,
  direction: 1 | -1,
  fy: FyStartMonth
): string {
  const start = periodStartDate(type, value, fy)
  const shifted = new Date(
    start.getFullYear(),
    start.getMonth() + direction * MONTHS_IN_PERIOD[type],
    1
  )
  return periodValueForDate(type, shifted, fy)
}

export function formatPeriodLabel(type: BudgetPeriodType, value: string, fy: FyStartMonth): string {
  const start = periodStartDate(type, value, fy)
  const startMonth = start.getMonth()
  const lastMonth = (startMonth + MONTHS_IN_PERIOD[type] - 1) % 12
  switch (type) {
    case 'MONTHLY':
      return `${MONTH_NAMES[startMonth]} ${start.getFullYear()}`
    case 'QUARTERLY': {
      const [y, q] = value.split('-Q').map(Number)
      return `Q${q} ${financialYearLabel(y, fy)} (${MONTH_SHORT[startMonth]}–${MONTH_SHORT[lastMonth]})`
    }
    case 'HALF_YEARLY': {
      const [y, h] = value.split('-H').map(Number)
      return `H${h} ${financialYearLabel(y, fy)} (${MONTH_SHORT[startMonth]}–${MONTH_SHORT[lastMonth]})`
    }
    case 'YEARLY':
      return financialYearLabel(Number(value), fy)
  }
}

/** A period's first and last day. */
export function periodBounds(
  type: BudgetPeriodType,
  value: string,
  fy: FyStartMonth
): { start: Date; end: Date } {
  const start = periodStartDate(type, value, fy)
  return { start, end: new Date(start.getFullYear(), start.getMonth() + MONTHS_IN_PERIOD[type], 0) }
}

/** The Transactions-page filter for a budget period: a month as `month`, longer periods as a range. */
export function periodTransactionFilter(
  type: BudgetPeriodType,
  value: string,
  fy: FyStartMonth
): { month: string } | { dateFrom: string; dateTo: string } {
  if (type === 'MONTHLY') return { month: value }
  const { start, end } = periodBounds(type, value, fy)
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { dateFrom: iso(start), dateTo: iso(end) }
}
